import supabase from '../lib/supabase';

export const appointmentService = {
  // Get all appointment types
  async getAppointmentTypes() {
    try {
      const { data, error } = await supabase
        .from('appointment_types_bp2025')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Get appointment types error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get only enabled appointment types (for booking)
  async getEnabledAppointmentTypes() {
    try {
      const { data, error } = await supabase
        .from('appointment_types_bp2025')
        .select('*')
        .eq('is_enabled', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Get enabled appointment types error:', error);
      return { success: false, error: error.message };
    }
  },

  // Toggle appointment type status (enable/disable)
  async toggleAppointmentTypeStatus(id, isEnabled) {
    try {
      console.log('Toggling appointment status:', { id, isEnabled });

      const { data, error } = await supabase
        .from('appointment_types_bp2025')
        .update({ 
          is_enabled: isEnabled,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Supabase toggle error:', error);
        throw error;
      }

      console.log('Successfully toggled appointment status:', data);
      return { success: true, data };
    } catch (error) {
      console.error('Toggle appointment status error:', error);
      return { success: false, error: error.message };
    }
  },

  // Create new appointment type
  async createAppointmentType(appointmentData) {
    try {
      // Prepare the data for insertion
      const insertData = {
        name: appointmentData.name,
        duration: parseInt(appointmentData.duration),
        consultation_fee: parseInt(appointmentData.consultationFee),
        available_days: appointmentData.availableDays,
        time_slots: appointmentData.timeSlots,
        location: appointmentData.location,
        google_maps_url: appointmentData.googleMapsUrl || null,
        additional_info: appointmentData.additionalInfo || null,
        is_enabled: appointmentData.isEnabled !== undefined ? appointmentData.isEnabled : true, // Default to enabled
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      console.log('Creating appointment with data:', insertData);

      const { data, error } = await supabase
        .from('appointment_types_bp2025')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }

      console.log('Successfully created appointment:', data);
      return { success: true, data };
    } catch (error) {
      console.error('Create appointment type error:', error);
      return { success: false, error: error.message };
    }
  },

  // Update appointment type
  async updateAppointmentType(id, appointmentData) {
    try {
      // Prepare the data for update
      const updateData = {
        name: appointmentData.name,
        duration: parseInt(appointmentData.duration),
        consultation_fee: parseInt(appointmentData.consultationFee),
        available_days: appointmentData.availableDays,
        time_slots: appointmentData.timeSlots,
        location: appointmentData.location,
        google_maps_url: appointmentData.googleMapsUrl || null,
        additional_info: appointmentData.additionalInfo || null,
        is_enabled: appointmentData.isEnabled !== undefined ? appointmentData.isEnabled : true,
        updated_at: new Date().toISOString()
      };

      console.log('Updating appointment with data:', updateData);

      const { data, error } = await supabase
        .from('appointment_types_bp2025')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }

      console.log('Successfully updated appointment:', data);
      return { success: true, data };
    } catch (error) {
      console.error('Update appointment type error:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete appointment type
  async deleteAppointmentType(id) {
    try {
      console.log('Deleting appointment with id:', id);

      const { error } = await supabase
        .from('appointment_types_bp2025')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Supabase delete error:', error);
        throw error;
      }

      console.log('Successfully deleted appointment');
      return { success: true };
    } catch (error) {
      console.error('Delete appointment type error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get appointment statistics
  async getAppointmentStats() {
    try {
      const [totalResult, enabledResult, disabledResult] = await Promise.all([
        supabase
          .from('appointment_types_bp2025')
          .select('*', { count: 'exact', head: true }),
        supabase
          .from('appointment_types_bp2025')
          .select('*', { count: 'exact', head: true })
          .eq('is_enabled', true),
        supabase
          .from('appointment_types_bp2025')
          .select('*', { count: 'exact', head: true })
          .eq('is_enabled', false)
      ]);

      const stats = {
        totalTypes: totalResult.count || 0,
        enabledTypes: enabledResult.count || 0,
        disabledTypes: disabledResult.count || 0
      };

      return {
        success: true,
        data: stats
      };
    } catch (error) {
      return { 
        success: true,
        data: {
          totalTypes: 0,
          enabledTypes: 0,
          disabledTypes: 0
        }
      };
    }
  },

  // Test connection to Supabase
  async testConnection() {
    try {
      const { data, error } = await supabase
        .from('appointment_types_bp2025')
        .select('count', { count: 'exact', head: true });

      if (error) {
        console.error('Connection test error:', error);
        throw error;
      }

      console.log('Supabase connection successful');
      return { success: true, message: 'Connected to Supabase successfully' };
    } catch (error) {
      console.error('Connection test failed:', error);
      return { success: false, error: error.message };
    }
  },

  // Initialize table with is_enabled column if not exists
  async initializeTable() {
    try {
      // Try to add the is_enabled column if it doesn't exist
      const { error } = await supabase.rpc('add_is_enabled_column');
      
      if (error && !error.message.includes('already exists')) {
        console.error('Error adding is_enabled column:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Initialize table error:', error);
      return { success: false, error: error.message };
    }
  }
};